import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  Globe,
  ShieldCheck,
  FileText,
  Buildings,
  Briefcase,
} from "@phosphor-icons/react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";

const heroImg =
  "https://images.unsplash.com/photo-1615406020658-6c4b805f1f30?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBidWlsZGluZyUyMGZhY2FkZXxlbnwwfHx8fDE3ODEyNjkzMzN8MA&ixlib=rb-4.1.0&q=85";

const structures = [
  {
    title: "Private Limited Company",
    desc: "Ideal for large-scale operations, investment rounds and multiple stakeholders. Offers limited liability and flexibility for raising capital.",
    icon: Buildings,
  },
  {
    title: "Limited Liability Partnership (LLP)",
    desc: "Suitable for joint ventures or smaller operations. Combines operational flexibility with limited liability for partners.",
    icon: Briefcase,
  },
  {
    title: "Branch / Liaison / Project Office",
    desc: "Branch for business activities, liaison for market research and promotion, project office for specific contracts.",
    icon: Globe,
  },
];

const steps = [
  {
    h: "Digital Signature Certificate (DSC)",
    p: "All directors and authorized signatories obtain DSCs to sign documents electronically.",
  },
  {
    h: "Director Identification Number (DIN)",
    p: "Each director secures a DIN for legal recognition under the Companies Act.",
  },
  {
    h: "Name Approval",
    p: "Proposed company names are submitted to the Ministry of Corporate Affairs (MCA) and must comply with Indian naming guidelines.",
  },
  {
    h: "Filing Incorporation Documents",
    p: "MOA, AOA, board resolutions and parent company incorporation certificates are filed with the RoC.",
  },
  {
    h: "RBI & FEMA Compliance",
    p: "Approval for foreign direct investment (FDI) and adherence to FEMA regulations.",
  },
  {
    h: "Certificate of Incorporation",
    p: "The RoC issues the Certificate of Incorporation, formally recognising the foreign business entity.",
  },
];

const documents = [
  "Identity and address proof of directors",
  "Certificate of Incorporation of the parent company",
  "Board resolution approving registration in India",
  "Registered office address proof in India",
  "FDI / FEMA approvals where applicable",
];

const compliance = [
  "Annual filings with the Registrar of Companies (RoC)",
  "GST registration and periodic tax compliance",
  "Labour law and employment compliance",
  "RBI reporting obligations for foreign investments",
  "Intellectual property registration and corporate governance",
];

const benefits = [
  {
    h: "Market Access",
    p: "Tap into India's growing consumer base and strategic location.",
  },
  {
    h: "Limited Liability Protection",
    p: "Safeguards investors from personal liability for business debts.",
  },
  {
    h: "Government Incentives",
    p: "Eligibility for FDI-related benefits and startup or business schemes.",
  },
  {
    h: "Credibility",
    p: "Enhances trust with partners, clients and financial institutions in India.",
  },
  {
    h: "Operational Flexibility",
    p: "Choose the structure that aligns with your goals — company, LLP or branch office.",
  },
];

const whyAACO = [
  "Expert advisory on corporate law and FDI regulations",
  "End-to-end support for document preparation, drafting and filing",
  "Guidance on registering as a foreign entity and compliance with Indian laws",
  "Assistance with ongoing statutory compliance and regulatory reporting",
  "Tailored advisory for market entry, investment structuring and operational strategy",
];

export default function ForeignEntityRegistration() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main data-testid="foreign-entity-page" className="bg-[#FDFBF7] text-[#1B3B2B]">
      <Header />

      {/* Hero */}
      <section className="relative pt-32 md:pt-40 pb-20 md:pb-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-end">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7"
          >
            <Link
              to="/"
              data-testid="back-to-home"
              className="inline-flex items-center gap-2 overline text-[10px] mb-8 text-[#4A5D53] hover:text-[#D4AF37] transition-colors"
            >
              <ArrowLeft size={12} weight="bold" /> Back to home
            </Link>
            <div className="overline mb-5">/ Service · Foreign Entity Registration</div>
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-[#1B3B2B]">
              Registering as a foreign entity in India.
            </h1>
            <p className="mt-8 max-w-xl text-[#4A5D53] text-base sm:text-lg leading-relaxed">
              Legal recognition for your business under Indian corporate laws — a separate legal
              identity, limited personal liability and a credible footing to operate, raise capital
              and access government schemes in India.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a href="/#contact" className="btn-primary">
                Speak to a partner <ArrowRight size={16} weight="bold" />
              </a>
              <Link to="/" className="btn-secondary">
                All services
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="lg:col-span-5"
          >
            <div className="aspect-[4/5] overflow-hidden bg-[#1B3B2B]">
              <img
                src={heroImg}
                alt="Architectural facade representing institutional credibility"
                className="w-full h-full object-cover opacity-95"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20 md:py-28 bg-[#F3F0EA]">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="overline mb-5">/ Overview</div>
          <h2 className="font-serif-display text-3xl sm:text-4xl leading-tight text-[#1B3B2B] mb-8">
            A separate legal identity for your business in India.
          </h2>
          <div className="space-y-5 text-[#4A5D53] text-base leading-relaxed">
            <p>
              Registering as a foreign entity in India involves obtaining legal recognition for your
              business under Indian corporate laws. This process gives your company a separate
              legal identity, limits personal liability for investors, and allows your business to
              operate officially in India. Foreign investors can leverage this registration to
              participate in government schemes, access funding opportunities, and gain credibility
              with local stakeholders.
            </p>
            <p>
              There are multiple structures available for foreign company registration, and the
              right choice depends on the nature of your business, investment plans and operational
              goals. AACO provides expert advisory services to help foreign entrepreneurs select
              the optimal legal structure while ensuring compliance with India's regulatory
              framework.
            </p>
          </div>
        </div>
      </section>

      {/* Structures */}
      <section className="py-20 md:py-28 bg-[#FDFBF7]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-12">
            <div className="lg:col-span-8">
              <div className="overline mb-5">/ Legal Structures</div>
              <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-tight text-[#1B3B2B]">
                Available structures for foreign business registration in India.
              </h2>
            </div>
            <p className="lg:col-span-4 text-[#4A5D53]">
              We help you choose the structure aligned to your investment and operational
              objectives — and shoulder the filings end-to-end.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {structures.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={s.title} className="card-flat p-8 flex flex-col" data-testid={`structure-${i}`}>
                  <Icon size={30} weight="duotone" className="text-[#1B3B2B]" />
                  <h3 className="font-serif-display text-2xl mt-8 leading-snug text-[#1B3B2B]">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm text-[#4A5D53] leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 md:py-28 bg-[#EBE7DF]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="overline mb-5">/ Process</div>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-tight text-[#1B3B2B] mb-12 max-w-3xl">
            Step-by-step foreign company registration process.
          </h2>
          <ol className="space-y-px bg-[#D1CEC5] border border-[#D1CEC5]">
            {steps.map((s, i) => (
              <li key={s.h} className="bg-[#FDFBF7] p-8 md:p-10 grid grid-cols-1 md:grid-cols-12 gap-6">
                <div className="md:col-span-2 overline text-[10px]">Step {String(i + 1).padStart(2, "0")}</div>
                <div className="md:col-span-10">
                  <h3 className="font-serif-display text-2xl text-[#1B3B2B] leading-snug">
                    {s.h}
                  </h3>
                  <p className="mt-2 text-sm text-[#4A5D53] leading-relaxed max-w-3xl">{s.p}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Documents & Compliance */}
      <section className="py-20 md:py-28 bg-[#FDFBF7]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          <div>
            <div className="overline mb-5 flex items-center gap-2">
              <FileText size={14} weight="duotone" /> Required Documents
            </div>
            <h2 className="font-serif-display text-3xl sm:text-4xl leading-tight text-[#1B3B2B] mb-8">
              Documents we prepare and review.
            </h2>
            <ul className="space-y-4">
              {documents.map((d) => (
                <li key={d} className="flex gap-3 items-start text-[#1B3B2B] text-sm">
                  <CheckCircle size={20} weight="duotone" className="mt-0.5 text-[#D4AF37] shrink-0" />
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="overline mb-5 flex items-center gap-2">
              <ShieldCheck size={14} weight="duotone" /> Post-Registration Compliance
            </div>
            <h2 className="font-serif-display text-3xl sm:text-4xl leading-tight text-[#1B3B2B] mb-8">
              Compliance after incorporation.
            </h2>
            <ul className="space-y-4">
              {compliance.map((d) => (
                <li key={d} className="flex gap-3 items-start text-[#1B3B2B] text-sm">
                  <CheckCircle size={20} weight="duotone" className="mt-0.5 text-[#D4AF37] shrink-0" />
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 md:py-28 bg-[#1B3B2B] text-[#FDFBF7]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="overline mb-5 text-[#D4AF37]">/ Benefits</div>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-tight text-[#FDFBF7] mb-12 max-w-3xl">
            Why register as a foreign company in India.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-[#D4AF37]/30 border border-[#D4AF37]/30">
            {benefits.map((b, i) => (
              <div key={b.h} className="bg-[#1B3B2B] p-7" data-testid={`benefit-${i}`}>
                <div className="overline text-[10px] text-[#D4AF37]">{String(i + 1).padStart(2, "0")}</div>
                <h3 className="font-serif-display text-xl mt-4 leading-snug text-[#FDFBF7]">
                  {b.h}
                </h3>
                <p className="mt-3 text-sm text-[#D8D2C2] leading-relaxed">{b.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why AACO */}
      <section className="py-20 md:py-28 bg-[#F3F0EA]">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="overline mb-5">/ Why AACO</div>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-tight text-[#1B3B2B] mb-10">
            A trusted legal partner for foreign investors in India.
          </h2>
          <p className="text-[#4A5D53] leading-relaxed max-w-2xl mb-8">
            AACO supports international businesses through the Indian corporate landscape — from
            entity selection and incorporation to long-term operational compliance.
          </p>
          <ul className="space-y-4">
            {whyAACO.map((d) => (
              <li key={d} className="flex gap-3 items-start text-[#1B3B2B]">
                <CheckCircle size={20} weight="duotone" className="mt-0.5 text-[#D4AF37] shrink-0" />
                <span className="text-sm leading-relaxed">{d}</span>
              </li>
            ))}
          </ul>

          <div className="mt-12 p-8 md:p-10 bg-[#FDFBF7] border border-[#D1CEC5]">
            <div className="overline text-[10px] mb-3">/ Begin</div>
            <h3 className="font-serif-display text-2xl md:text-3xl text-[#1B3B2B] leading-snug mb-4">
              Plan your India entry with AACO.
            </h3>
            <p className="text-[#4A5D53] text-sm leading-relaxed max-w-2xl mb-6">
              From entity selection to post-registration compliance — write to us with a brief on
              your business, and a partner will respond within one business day.
            </p>
            <a href="/#contact" className="btn-primary" data-testid="fer-contact-cta">
              Speak to a partner <ArrowRight size={16} weight="bold" />
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
